import { useEffect, useState, useRef } from 'react';
import { fetchEntitlement } from './api';
import type { EntitlementResult } from '@/hooks/useEntitlement';

/**
 * D-10 Bestellung-erfolgreich-Page polling hook (Batch 11 Commit 6).
 *
 * Polls `GET /api/credits/entitlement` every 2 seconds for up to 30
 * seconds, detecting the post-checkout state-transition driven by
 * Stripe webhooks landing on the backend (`checkout.session.completed`
 * → `customer.subscription.created` / `invoice.paid` → DB write →
 * entitlement counters update).
 *
 * Baseline-detection strategy:
 *   - Capture `{ subscriptionRemaining, subscriptionPeriodEnd,
 *     packRemaining }` on the FIRST successful fetch (the baseline).
 *   - On each subsequent tick, compare the current snapshot to the
 *     baseline.
 *
 * Subscription-success: `subscriptionPeriodEnd` was null at baseline
 * AND is now non-null. (The baseline-vs-current diff matters because
 * a user with an active subscription who buys a pack would also have
 * `subscriptionPeriodEnd !== null` from the start — pack-success
 * needs to be discriminated independently.)
 *
 * Pack-success: `packRemaining` increased from the baseline. The grant
 * count is `current - baseline` and is surfaced for the success-state
 * copy ("{count} Geschichten").
 *
 * Tie-break: if both transitions fire on the same tick (rare —
 * back-to-back checkouts in <2s window), subscription-success wins.
 * Briefing K-11 doesn't address this combo; subscription is the
 * higher-cardinality event UX-wise.
 *
 * Lifecycle:
 *   - Cleanup clears both the interval and the timeout on unmount.
 *   - On state-transition detected: stop polling, do not set timeout.
 *   - On 30s elapsed without transition: stop polling, set 'timeout'.
 *   - On baseline-fetch network error: keep polling silently — the
 *     Stripe-webhook-driven state-update may still arrive within the
 *     timeout window. If the entire 30s elapses with only failures,
 *     'timeout' is the user-facing state.
 *
 * Per-tick errors during polling are silent — the user sees the
 * "wir richten alles ein" copy until either success or timeout.
 */

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 30_000;

export type CheckoutPollingPhase =
  | { kind: 'polling' }
  | {
      kind: 'success-subscription';
      subscriptionRemaining: number;
      subscriptionPeriodEnd: string;
    }
  | { kind: 'success-pack'; packCreditsGranted: number }
  | { kind: 'timeout' };

interface UseCheckoutPollingOptions {
  /**
   * Override the 2s poll interval. Test-only — production keeps the
   * default to avoid hammering the backend.
   */
  pollIntervalMs?: number;
  /**
   * Override the 30s timeout. Test-only — Briefing-spec'd duration
   * for production (do not extend without briefing change).
   */
  timeoutMs?: number;
  /**
   * When `false`, the hook does NOT start polling — it returns
   * `{ kind: 'polling' }` (the initial state) and never fetches.
   * Used by the D-10 `/checkout/success` fast-path (Mini-Commit 6.3):
   * if the session-status endpoint returns 'completed' on mount, the
   * route renders the success-state from the session response and
   * skips polling entirely. Default `true` (poll).
   */
  enabled?: boolean;
}

export function useCheckoutPolling(options: UseCheckoutPollingOptions = {}): CheckoutPollingPhase {
  const pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? TIMEOUT_MS;
  const enabled = options.enabled ?? true;

  const [phase, setPhase] = useState<CheckoutPollingPhase>({ kind: 'polling' });
  const baselineRef = useRef<EntitlementResult | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let intervalHandle: ReturnType<typeof setInterval> | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    function stopPolling() {
      if (intervalHandle !== null) {
        clearInterval(intervalHandle);
        intervalHandle = null;
      }
      if (timeoutHandle !== null) {
        clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
    }

    function checkTransition(current: EntitlementResult): CheckoutPollingPhase | null {
      const baseline = baselineRef.current;
      if (!baseline) return null;
      const subscriptionActivated =
        baseline.subscriptionPeriodEnd === null && current.subscriptionPeriodEnd !== null;
      if (subscriptionActivated && current.subscriptionPeriodEnd !== null) {
        return {
          kind: 'success-subscription',
          subscriptionRemaining: current.subscriptionRemaining,
          subscriptionPeriodEnd: current.subscriptionPeriodEnd,
        };
      }
      const packIncreased = current.packRemaining > baseline.packRemaining;
      if (packIncreased) {
        return {
          kind: 'success-pack',
          packCreditsGranted: current.packRemaining - baseline.packRemaining,
        };
      }
      return null;
    }

    async function pollOnce() {
      if (cancelled) return;
      try {
        const current = await fetchEntitlement();
        if (cancelled) return;
        if (!baselineRef.current) {
          baselineRef.current = current;
          return;
        }
        const transition = checkTransition(current);
        if (transition) {
          stopPolling();
          setPhase(transition);
        }
      } catch {
        // Silent: keep polling. Timeout will catch the all-failed case.
      }
    }

    // Initial fetch + start interval immediately so the user doesn't
    // wait 2s for the first network call.
    void pollOnce();
    intervalHandle = setInterval(() => void pollOnce(), pollIntervalMs);
    timeoutHandle = setTimeout(() => {
      if (cancelled) return;
      stopPolling();
      setPhase({ kind: 'timeout' });
    }, timeoutMs);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [pollIntervalMs, timeoutMs, enabled]);

  return phase;
}
